import type { ActionInput, ActionName, Message } from "@agency/core";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Brain, Check, Compass, FileDown, History, Loader2, MessageSquarePlus, Paperclip, Pencil, Search, Send, ShieldCheck, Sparkles, Trash2, X, Zap } from "lucide-react";
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { ModeBadge } from "../components/ModeBadge";
import { Button, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { Markdown } from "./Markdown";
import {
  closeAssistant, deleteConversation, getConversation, listConversations, prepareAttachment, renameConversation, setMode as saveMode, streamAssistant,
  type AssistantEvent, type AssistantMode, type Attachment, type ConversationSummary, type StoredConversation,
} from "./stream";

/**
 * Faraday panel: a conversation with the assistant. The model runs server-side as the signed-in person and acts
 * through the same action API as the screens; this panel only renders what came back: streamed text, one line per
 * tool call with its receipt (operation id, mode, Open link), message drafts with a Send button, changes that wait
 * for a Confirm button (and run as the person when pressed), filed and generated files, remembered facts, and
 * screens the assistant opened. Photos and files attach to a message and are read by the model that turn.
 *
 * Conversations are stored server-side per person. The History drawer lists them (open, rename, delete); the
 * header switch picks the person's mode: Ask first (every change waits for Confirm) or Act (ordinary changes run).
 */

interface ToolLine { id: string; label: string; status: "start" | "done"; operationId?: string; executionMode?: string; recordUrl?: string; mutates: boolean; error?: string }
interface Pending { id: string; customerId: string; customerName: string; channel: "sms" | "email"; body: string; subject?: string; policyId?: string; consent: boolean; outcome?: string; mode?: string }
interface PendingAction { id: string; action: string; label: string; input: Record<string, unknown>; outcome?: string; mode?: string; recordUrl?: string; operationId?: string; error?: boolean }
interface FileLine { id: string; documentId: string; title: string; url: string }
interface MemoryLine { id: string; text: string; removed?: boolean }
interface Msg { id: number; who: "user" | "assistant"; text: string; attachments?: Attachment[]; tools: ToolLine[]; pendings: Pending[]; actions: PendingAction[]; files: FileLine[]; memories: MemoryLine[]; error?: string; streaming?: boolean }

const CONVERSATION_KEY = "agency.assistant.conversation";
const empty = (): Pick<Msg, "tools" | "pendings" | "actions" | "files" | "memories"> => ({ tools: [], pendings: [], actions: [], files: [], memories: [] });

/** The one reducer for events, used live and when replaying a stored conversation. */
function applyEvent(m: Msg, ev: AssistantEvent, expiredLabel?: string): Msg {
  switch (ev.type) {
    case "text": return { ...m, text: m.text + ev.delta };
    case "tool":
      if (ev.status === "start") return { ...m, tools: [...m.tools, { id: ev.id, label: ev.label, status: "start", mutates: false }] };
      return m.tools.some((x) => x.id === ev.id)
        ? { ...m, tools: m.tools.map((x) => (x.id === ev.id ? { ...x, status: "done", operationId: ev.operationId, executionMode: ev.executionMode, recordUrl: ev.recordUrl, mutates: ev.mutates, error: ev.error } : x)) }
        : { ...m, tools: [...m.tools, { id: ev.id, label: ev.label, status: "done", operationId: ev.operationId, executionMode: ev.executionMode, recordUrl: ev.recordUrl, mutates: ev.mutates, error: ev.error }] };
    case "pending_text": return { ...m, pendings: [...m.pendings, { ...ev, outcome: expiredLabel }] };
    case "pending_action": return { ...m, actions: [...m.actions, { id: ev.id, action: ev.action, label: ev.label, input: ev.input, outcome: expiredLabel }] };
    case "file": return { ...m, files: [...m.files, { ...ev }] };
    case "memory": return { ...m, memories: [...m.memories, { id: ev.id, text: ev.text, removed: ev.removed }] };
    case "error": return { ...m, error: ev.message };
    default: return m;
  }
}

export function FaradayPanel() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const assistant = config.connectors.find((c) => c.id === "assistant");
  const ready = assistant?.state === "live";
  const welcome = (): Msg => ({ id: 0, who: "assistant", text: t(ready ? "faraday.welcome" : "faraday.notConfigured"), ...empty() });
  const [msgs, setMsgs] = useState<Msg[]>(() => [welcome()]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [mode, setModeState] = useState<AssistantMode>("ask");
  const [conversationId, setConversationId] = useState<string | null>(() => { try { return sessionStorage.getItem(CONVERSATION_KEY); } catch { return null; } });
  const [title, setTitle] = useState<string>("");
  const [drawer, setDrawer] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const notes = useRef<string[]>([]);
  const counter = useRef(1);
  const bottom = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const convRef = useRef<string | null>(conversationId);
  convRef.current = conversationId;

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => { try { if (conversationId) sessionStorage.setItem(CONVERSATION_KEY, conversationId); else sessionStorage.removeItem(CONVERSATION_KEY); } catch { /* fine */ } }, [conversationId]);

  // First load: the person's mode and, if the tab was in the middle of a conversation, that conversation.
  useEffect(() => {
    if (!ready) return;
    void refreshList();
    if (conversationId) void open(conversationId, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const refreshList = async () => {
    setLoadingList(true);
    try { const r = await listConversations(); setConversations(r.conversations); setModeState(r.prefs.mode); } catch { /* list stays as is */ } finally { setLoadingList(false); }
  };

  const invalidate = () => {
    for (const key of ["today", "customers", "customer", "inbox", "thread", "documents", "forms", "form-drafts", "carriers", "renewals", "pipeline", "policies", "reports", "commissions", "imports", "import", "settings", "users", "quote", "config"]) qc.invalidateQueries({ queryKey: [key] });
  };

  const patchLast = (fn: (m: Msg) => Msg) => setMsgs((xs) => xs.map((m, i) => (i === xs.length - 1 ? fn(m) : m)));
  const patchMsg = (msgId: number, fn: (m: Msg) => Msg) => setMsgs((xs) => xs.map((m) => (m.id === msgId ? fn(m) : m)));

  /** Send a message, or (with no text and pending notes) let the assistant continue after a Confirm. */
  const ask = async (text: string, opts: { continuation?: boolean } = {}) => {
    const message = text.trim();
    const files = attachments;
    const sentNotes = notes.current;
    if ((!message && !files.length && !(opts.continuation && sentNotes.length)) || busy || !ready) return;
    setInput("");
    setAttachments([]);
    setBusy(true);
    notes.current = [];
    const assistantId = counter.current++;
    setMsgs((xs) => [...xs, ...(opts.continuation ? [] : [{ id: counter.current++, who: "user" as const, text: message, attachments: files, ...empty() }]), { id: assistantId, who: "assistant" as const, text: "", ...empty(), streaming: true }]);
    let mutated = false;
    abort.current = new AbortController();
    const onEvent = (ev: AssistantEvent) => {
      if (ev.type === "navigate") { navigate(ev.path); return; }
      if (ev.type === "done") { setConversationId(ev.conversationId); setTitle(ev.title); return; }
      if (ev.type === "tool" && ev.status === "done" && ev.mutates && !ev.error) mutated = true;
      if (ev.type === "file") mutated = true;
      patchLast((m) => applyEvent(m, ev));
    };
    try {
      await streamAssistant({ conversationId: convRef.current ?? undefined, message, page: location.pathname, locale, attachments: files.map(({ name, mediaType, data }) => ({ name, mediaType, data })), notes: sentNotes }, onEvent, abort.current.signal);
    } catch (err) {
      if ((err as Error).name !== "AbortError") patchLast((m) => ({ ...m, error: (err as Error).message }));
    } finally {
      patchLast((m) => ({ ...m, streaming: false }));
      setBusy(false);
      if (mutated) invalidate();
      void refreshList();
    }
  };

  const submit = (e: FormEvent) => { e.preventDefault(); void ask(input); };

  const pickFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    const next: Attachment[] = [];
    for (const f of list.slice(0, 4 - attachments.length)) {
      try { next.push(await prepareAttachment(f)); } catch (err) { patchLast((m) => ({ ...m, error: (err as Error).message })); }
    }
    setAttachments((xs) => [...xs, ...next].slice(0, 4));
    inputRef.current?.focus();
  };

  const sendPending = async (msgId: number, p: Pending) => {
    setBusy(true);
    try {
      const r = await callAction<"agency.message.send", Message>("agency.message.send", { customerId: p.customerId, channel: p.channel, body: p.body, subject: p.subject, policyId: p.policyId, category: "transactional", aiDrafted: true, idempotencyKey: `assistant:${p.id}` }, { source: "faraday" });
      const m = r.result;
      const outcome = `${t("faraday.sentStatus")}: ${t(`inbox.status.${m.status}`)}`;
      patchMsg(msgId, (x) => ({ ...x, pendings: x.pendings.map((q) => (q.id === p.id ? { ...q, outcome, mode: r.executionMode } : q)) }));
      notes.current.push(`The person pressed Send on the ${p.channel} draft to ${p.customerName}; server status: ${m.status}${r.executionMode ? ` (${r.executionMode})` : ""}.`);
      invalidate();
    } catch (err) {
      patchMsg(msgId, (x) => ({ ...x, pendings: x.pendings.map((q) => (q.id === p.id ? { ...q, outcome: `${t("common.error")} ${(err as Error).message}` } : q)) }));
      notes.current.push(`Sending the ${p.channel} draft to ${p.customerName} failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const discard = (msgId: number, p: Pending) => {
    patchMsg(msgId, (x) => ({ ...x, pendings: x.pendings.filter((q) => q.id !== p.id) }));
    notes.current.push(`The person discarded the ${p.channel} draft to ${p.customerName} without sending.`);
  };

  const confirmAction = async (msgId: number, a: PendingAction) => {
    setBusy(true);
    let ok = false;
    try {
      const r = await callAction(a.action as ActionName, a.input as ActionInput<ActionName>, { source: "faraday" });
      patchMsg(msgId, (x) => ({ ...x, actions: x.actions.map((q) => (q.id === a.id ? { ...q, outcome: t("faraday.confirmed"), mode: r.executionMode, recordUrl: r.recordUrl, operationId: r.operationId } : q)) }));
      notes.current.push(`The person confirmed "${a.label}" (${a.action}); it ran with receipt ${r.operationId}${r.recordUrl ? `, record at ${r.recordUrl}` : ""}. Result: ${JSON.stringify(r.result ?? {}).slice(0, 400)}`);
      invalidate();
      ok = true;
    } catch (err) {
      patchMsg(msgId, (x) => ({ ...x, actions: x.actions.map((q) => (q.id === a.id ? { ...q, outcome: `${t("common.error")} ${(err as Error).message}`, error: true } : q)) }));
      notes.current.push(`The person confirmed "${a.label}" (${a.action}) but it failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
    // Let the assistant carry on (the next card, the link, or "done") without the person having to type.
    if (ok) void ask("", { continuation: true });
  };

  const discardAction = (msgId: number, a: PendingAction) => {
    patchMsg(msgId, (x) => ({ ...x, actions: x.actions.filter((q) => q.id !== a.id) }));
    notes.current.push(`The person discarded the "${a.label}" card (${a.action}); nothing was changed.`);
  };

  const startNew = () => {
    abort.current?.abort();
    const old = convRef.current;
    setConversationId(null);
    setTitle("");
    notes.current = [];
    setAttachments([]);
    setMsgs([{ ...welcome(), id: counter.current++ }]);
    setDrawer(false);
    if (old) void closeAssistant(old);
    inputRef.current?.focus();
  };

  /** Replay a stored conversation into the panel. Old drafts and cards show without buttons. */
  const open = async (id: string, opts: { silent?: boolean } = {}) => {
    if (busy) return;
    try {
      const { conversation } = await getConversation(id);
      const expired = t("faraday.expired");
      const out: Msg[] = [];
      for (const turn of conversation.turns) {
        if (turn.user.text || turn.user.attachments.length) out.push({ id: counter.current++, who: "user", text: turn.user.text, attachments: turn.user.attachments.map((a) => ({ ...a, data: "" })), ...empty() });
        let m: Msg = { id: counter.current++, who: "assistant", text: "", ...empty() };
        for (const ev of turn.events) m = applyEvent(m, ev, expired);
        out.push(m);
      }
      setMsgs(out.length ? out : [{ ...welcome(), id: counter.current++ }]);
      setConversationId(conversation.id);
      setTitle(conversation.title);
      notes.current = [];
      setDrawer(false);
    } catch (err) {
      if (!opts.silent) patchLast((m) => ({ ...m, error: (err as Error).message }));
      else { setConversationId(null); setTitle(""); }
    }
  };

  const rename = async (id: string, next: string) => {
    const clean = next.trim();
    if (!clean) return;
    try {
      const r = await renameConversation(id, clean);
      setConversations((xs) => xs.map((c) => (c.id === id ? r.conversation : c)));
      if (id === conversationId) setTitle(r.conversation.title);
    } catch { /* keep the old title */ }
  };

  const remove = async (id: string) => {
    try {
      await deleteConversation(id);
      setConversations((xs) => xs.filter((c) => c.id !== id));
      if (id === conversationId) { setConversationId(null); setTitle(""); setMsgs([{ ...welcome(), id: counter.current++ }]); }
    } catch { /* stays listed */ }
  };

  const switchMode = async (next: AssistantMode) => {
    if (next === mode) return;
    setModeState(next);
    try { await saveMode(next); } catch { setModeState(mode); }
  };

  const chips: Array<{ label: string; text: string; send: boolean; attach?: boolean; icon?: "clip" | "compass" }> = [
    { label: t("faraday.chip.brief"), text: t("faraday.chip.brief.text"), send: true },
    { label: t("faraday.chip.renewals"), text: t("faraday.chip.renewals.text"), send: true },
    { label: t("faraday.chip.find"), text: t("faraday.chip.find.text"), send: false },
    { label: t("faraday.chip.add"), text: t("faraday.chip.add.text"), send: false },
    { label: t("faraday.chip.scan"), text: t("faraday.chip.scan.text"), send: false, attach: true, icon: "clip" },
    { label: t("faraday.chip.text"), text: t("faraday.chip.text.text"), send: false },
    { label: t("faraday.chip.report"), text: t("faraday.chip.report.text"), send: true },
    { label: t("faraday.chip.walk"), text: t("faraday.chip.walk.text"), send: false, icon: "compass" },
  ];
  const useChip = (c: (typeof chips)[number]) => {
    if (c.attach) { setInput(c.text); fileRef.current?.click(); return; }
    if (c.send) void ask(c.text);
    else { setInput(c.text); inputRef.current?.focus(); }
  };

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-ink/10">
        <button type="button" onClick={() => { setDrawer(true); void refreshList(); }} aria-label={t("faraday.history")} title={t("faraday.history")} className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl text-ink/60 hover:bg-ink/5 hover:text-ink">
          <History className="h-5 w-5" />
        </button>
        <span className="h-8 w-8 shrink-0 rounded-full bg-ink text-white flex items-center justify-center"><Bot className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-bold leading-tight truncate">{title || t("faraday.title")}</p>
          <p className="text-[11px] text-ink/55 leading-tight truncate">{title ? t("faraday.title") : assistant?.provider ?? "faraday"}</p>
        </div>
        <ModeBadge mode={assistant?.mode ?? "fixture"} />
        <button type="button" onClick={startNew} aria-label={t("faraday.newChat")} title={t("faraday.newChat")} className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl text-ink/60 hover:bg-ink/5 hover:text-ink"><MessageSquarePlus className="h-5 w-5" /></button>
      </div>

      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-ink/10 bg-ink/[0.025]">
        <div role="radiogroup" aria-label={t("faraday.mode")} className="inline-flex rounded-full border border-ink/15 bg-white p-0.5 text-xs font-semibold">
          <button type="button" role="radio" aria-checked={mode === "ask"} title={t("faraday.mode.ask.hint")} onClick={() => switchMode("ask")} className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition", mode === "ask" ? "bg-ink text-white" : "text-ink/60 hover:text-ink")}>
            <ShieldCheck className="h-3.5 w-3.5" /> {t("faraday.mode.ask")}
          </button>
          <button type="button" role="radio" aria-checked={mode === "act"} title={t("faraday.mode.act.hint")} onClick={() => switchMode("act")} className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition", mode === "act" ? "bg-primary text-white" : "text-ink/60 hover:text-ink")}>
            <Zap className="h-3.5 w-3.5" /> {t("faraday.mode.act")}
          </button>
        </div>
        <p className="min-w-0 flex-1 truncate text-[11px] text-ink/55">{mode === "ask" ? t("faraday.mode.ask.hint") : t("faraday.mode.act.hint")}</p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 scrollbar-thin">
        {msgs.map((m) => (
          <div key={m.id} className={cn("max-w-[94%] rounded-2xl px-3 py-2 text-sm", m.who === "user" ? "ml-auto bg-primary text-white whitespace-pre-wrap" : "bg-surface border border-ink/8")}>
            {m.who === "user" ? (
              <>
                {m.attachments?.length ? (
                  <div className="mb-1 flex flex-wrap gap-1">
                    {m.attachments.map((a, i) => a.preview ? <img key={i} src={a.preview} alt={a.name} className="h-16 w-16 rounded-lg object-cover border border-white/40" /> : <span key={i} className="inline-flex items-center gap-1 rounded-lg bg-white/20 px-2 py-1 text-xs"><Paperclip className="h-3 w-3" />{a.name}</span>)}
                  </div>
                ) : null}
                {m.text}
              </>
            ) : (
              <>
                {m.tools.length ? (
                  <ul className="mb-2 space-y-1">
                    {m.tools.map((tl) => (
                      <li key={tl.id} className="rounded-xl bg-white border border-ink/10 px-2.5 py-1.5 text-xs">
                        <div className="flex items-center gap-1.5">
                          {tl.status === "start" ? <Loader2 className="h-3.5 w-3.5 animate-spin text-ink/50" /> : tl.error ? <span className="text-red-700 font-bold">!</span> : <Check className="h-3.5 w-3.5 text-primary-dark" />}
                          <span className={cn("font-semibold", tl.error && "text-red-700")}>{tl.label}</span>
                          {tl.status === "done" && tl.executionMode ? <ModeBadge mode={tl.executionMode} /> : null}
                          {tl.status === "done" && tl.recordUrl ? <Link to={tl.recordUrl} className="ml-auto font-semibold text-primary-dark hover:underline">{t("common.open")} →</Link> : null}
                        </div>
                        {tl.error ? <p className="mt-0.5 text-red-700">{tl.error}</p> : null}
                        {tl.status === "done" && tl.mutates && tl.operationId && !tl.error ? <p className="mt-0.5 font-mono text-[10px] text-ink/50 break-all">{t("faraday.receipt")} · {tl.operationId}</p> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {m.text ? <Markdown text={m.text} /> : m.streaming && !m.tools.length ? <Loader2 className="h-4 w-4 animate-spin text-ink/40" /> : null}
                {m.pendings.map((p) => (
                  <div key={p.id} className="mt-2 rounded-xl bg-white border border-ink/10 p-3">
                    <p className="text-xs font-bold text-ink/60">{p.channel.toUpperCase()} → {p.customerName}{p.subject ? ` · ${p.subject}` : ""}</p>
                    <p className="mt-1 whitespace-pre-wrap">{p.body}</p>
                    {!p.consent && p.channel === "sms" ? <p className="mt-1 text-xs text-amber-700">{t("faraday.noConsent")}</p> : null}
                    {p.outcome ? (
                      <p className="mt-2 flex items-center gap-2 text-xs font-semibold">{p.outcome} {p.mode ? <ModeBadge mode={p.mode} /> : null}</p>
                    ) : (
                      <>
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" onClick={() => sendPending(m.id, p)} loading={busy}><Send className="h-4 w-4" />{t("faraday.sendNow")}</Button>
                          <Button size="sm" variant="outline" onClick={() => discard(m.id, p)}>{t("faraday.discard")}</Button>
                        </div>
                        <p className="mt-1 text-[11px] text-ink/50">{t("faraday.draftReady")}</p>
                      </>
                    )}
                  </div>
                ))}
                {m.actions.map((a) => (
                  <div key={a.id} className="mt-2 rounded-xl bg-white border border-amber-300/70 p-3">
                    <p className="flex items-center gap-1.5 text-xs font-bold text-ink/70"><ShieldCheck className="h-4 w-4 text-amber-600" /> {a.label}</p>
                    <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                      {flatten(a.input).map(([k, v]) => (
                        <div key={k} className="contents"><dt className="font-mono text-ink/55">{k}</dt><dd className="break-words">{v}</dd></div>
                      ))}
                    </dl>
                    {a.outcome ? (
                      <p className={cn("mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold", a.error && "text-red-700")}>
                        {a.outcome} {a.mode ? <ModeBadge mode={a.mode} /> : null}
                        {a.recordUrl ? <Link to={a.recordUrl} className="font-semibold text-primary-dark hover:underline">{t("common.open")} →</Link> : null}
                        {a.operationId ? <span className="font-mono text-[10px] text-ink/50 break-all">{t("faraday.receipt")} · {a.operationId}</span> : null}
                      </p>
                    ) : (
                      <>
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" onClick={() => confirmAction(m.id, a)} loading={busy}><Check className="h-4 w-4" />{t("faraday.confirm")}</Button>
                          <Button size="sm" variant="outline" onClick={() => discardAction(m.id, a)}>{t("faraday.discard")}</Button>
                        </div>
                        <p className="mt-1 text-[11px] text-ink/50">{t("faraday.confirmHint")}</p>
                      </>
                    )}
                  </div>
                ))}
                {m.files.map((f) => (
                  <a key={f.id} href={f.url} target="_blank" rel="noopener" className="mt-2 flex items-center gap-2 rounded-xl bg-white border border-ink/10 px-3 py-2 text-sm font-semibold text-primary-dark hover:border-primary">
                    <FileDown className="h-4 w-4" /> {f.title}
                  </a>
                ))}
                {m.memories.map((mm) => (
                  <p key={mm.id} className="mt-2 flex items-start gap-1.5 text-xs text-ink/60"><Brain className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {mm.removed ? t("faraday.forgot") : `${t("faraday.remembered")}: ${mm.text}`}</p>
                ))}
                {m.error ? <p className="mt-2 text-xs text-red-700">{m.error}</p> : null}
              </>
            )}
          </div>
        ))}
        <div ref={bottom} />
      </div>

      <div className="border-t border-ink/10 px-3 pt-2">
        <p className="text-[11px] font-semibold text-ink/50 flex items-center gap-1"><Sparkles className="h-3 w-3" /> {t("faraday.chips")}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          {chips.map((c) => (
            <button key={c.label} type="button" disabled={!ready || busy} onClick={() => useChip(c)} className="min-h-[32px] rounded-full border border-ink/15 bg-white px-3 text-xs font-semibold hover:border-primary hover:text-primary-dark disabled:opacity-50">
              {c.icon === "clip" ? <Paperclip className="mr-1 inline h-3 w-3" /> : c.icon === "compass" ? <Compass className="mr-1 inline h-3 w-3" /> : null}{c.label}
            </button>
          ))}
        </div>
      </div>
      <form onSubmit={submit} className="p-3">
        {attachments.length ? (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {attachments.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-lg border border-ink/15 bg-white pl-1 pr-1 py-0.5 text-xs">
                {a.preview ? <img src={a.preview} alt="" className="h-8 w-8 rounded object-cover" /> : <Paperclip className="h-3.5 w-3.5 text-ink/50" />}
                <span className="max-w-[140px] truncate">{a.name}</span>
                <button type="button" aria-label={t("faraday.removeAttachment")} onClick={() => setAttachments((xs) => xs.filter((_, j) => j !== i))} className="rounded p-0.5 text-ink/50 hover:bg-ink/5 hover:text-ink"><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        ) : null}
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*,.pdf,.csv,.txt,application/pdf,text/csv,text/plain" multiple onChange={pickFiles} className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={!ready || busy || attachments.length >= 4} aria-label={t("faraday.attach")} title={t("faraday.attachHint")} className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl border border-ink/15 text-ink/60 hover:border-primary hover:text-primary-dark disabled:opacity-50">
            <Paperclip className="h-4 w-4" />
          </button>
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder={attachments.length ? t("faraday.attachPlaceholder") : t("faraday.placeholder")} aria-label={t("faraday.title")} disabled={!ready} className="flex-1 min-h-tap min-w-0 rounded-xl border border-ink/15 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-ink/5" />
          <button type="submit" aria-label={t("faraday.send")} disabled={busy || !ready || (!input.trim() && !attachments.length)} className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl bg-ink text-white hover:bg-ink/90 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-snug text-ink/50">{t("faraday.disclaimer")}</p>
      </form>

      <AnimatePresence>
        {drawer ? (
          <>
            <motion.div key="backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="absolute inset-0 z-10 bg-ink/30" onClick={() => setDrawer(false)} />
            <motion.div key="drawer" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", damping: 30, stiffness: 320 }} className="absolute inset-y-0 left-0 z-20 flex w-[92%] max-w-[320px] flex-col bg-white shadow-2xl border-r border-ink/10">
              <HistoryDrawer
                conversations={conversations} loading={loadingList} activeId={conversationId} locale={locale}
                onClose={() => setDrawer(false)} onNew={startNew} onOpen={(id) => void open(id)} onRename={rename} onDelete={remove}
              />
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** The conversation list: search, grouped by day, open on click, rename and delete inline. */
function HistoryDrawer({ conversations, loading, activeId, locale, onClose, onNew, onOpen, onRename, onDelete }: {
  conversations: ConversationSummary[]; loading: boolean; activeId: string | null; locale: "en" | "es";
  onClose: () => void; onNew: () => void; onOpen: (id: string) => void; onRename: (id: string, title: string) => Promise<void>; onDelete: (id: string) => Promise<void>;
}) {
  const t = useT();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const needle = q.trim().toLowerCase();
  const shown = needle ? conversations.filter((c) => c.title.toLowerCase().includes(needle) || c.preview.toLowerCase().includes(needle)) : conversations;
  const groups = groupByDay(shown, t);
  const time = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" });
  const day = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-ink/10">
        <History className="h-5 w-5 text-ink/60" />
        <p className="flex-1 font-bold">{t("faraday.history")}</p>
        <button type="button" onClick={onClose} aria-label={t("common.close")} className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl text-ink/60 hover:bg-ink/5 hover:text-ink"><X className="h-5 w-5" /></button>
      </div>
      <div className="px-3 pt-3 space-y-2">
        <Button className="w-full" onClick={onNew}><MessageSquarePlus className="h-4 w-4" /> {t("faraday.newChat")}</Button>
        <label className="flex items-center gap-2 rounded-xl border border-ink/15 bg-white px-2.5 min-h-[36px] text-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30">
          <Search className="h-4 w-4 text-ink/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("faraday.searchHistory")} aria-label={t("faraday.searchHistory")} className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-ink/40" />
        </label>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2 scrollbar-thin">
        {loading && !conversations.length ? <p className="px-2 py-6 text-center text-sm text-ink/50"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></p> : null}
        {!loading && !shown.length ? (
          <div className="px-3 py-8 text-center">
            <Sparkles className="mx-auto h-6 w-6 text-primary" />
            <p className="mt-2 text-sm font-semibold">{needle ? t("faraday.noMatches") : t("faraday.noHistory")}</p>
            {!needle ? <p className="mt-1 text-xs text-ink/55">{t("faraday.noHistoryHint")}</p> : null}
          </div>
        ) : null}
        {groups.map((g) => (
          <div key={g.label} className="mb-2">
            <p className="px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-ink/45">{g.label}</p>
            <ul className="space-y-0.5">
              {g.items.map((c) => {
                const active = c.id === activeId;
                const isEditing = editing?.id === c.id;
                const asking = confirmDelete === c.id;
                return (
                  <li key={c.id} className={cn("group rounded-xl border px-2.5 py-2 transition", active ? "border-primary/40 bg-primary/10" : "border-transparent hover:bg-ink/[0.04]")}>
                    {isEditing ? (
                      <form onSubmit={(e) => { e.preventDefault(); void onRename(c.id, editing.title); setEditing(null); }} className="flex items-center gap-1">
                        <input autoFocus value={editing.title} onChange={(e) => setEditing({ id: c.id, title: e.target.value })} onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }} aria-label={t("faraday.rename")} className="min-w-0 flex-1 rounded-lg border border-ink/20 px-2 py-1 text-sm focus:border-primary focus:outline-none" />
                        <button type="submit" aria-label={t("common.save")} className="rounded-lg p-1.5 text-primary-dark hover:bg-primary/10"><Check className="h-4 w-4" /></button>
                        <button type="button" aria-label={t("common.cancel")} onClick={() => setEditing(null)} className="rounded-lg p-1.5 text-ink/50 hover:bg-ink/5"><X className="h-4 w-4" /></button>
                      </form>
                    ) : (
                      <div className="flex items-start gap-1">
                        <button type="button" onClick={() => onOpen(c.id)} className="min-w-0 flex-1 text-left">
                          <p className={cn("truncate text-sm font-semibold", active && "text-primary-dark")}>{c.title}</p>
                          {c.preview ? <p className="mt-0.5 line-clamp-2 text-xs text-ink/55">{c.preview}</p> : null}
                          <p className="mt-1 text-[11px] text-ink/45">{g.showDate ? `${day.format(new Date(c.updatedAt))} · ` : ""}{time.format(new Date(c.updatedAt))} · {c.messageCount} {t(c.messageCount === 1 ? "faraday.message" : "faraday.messages")}</p>
                        </button>
                        <div className={cn("flex shrink-0 flex-col gap-0.5 opacity-70 transition group-hover:opacity-100 md:opacity-0", (active || asking) && "md:opacity-100")}>
                          <button type="button" aria-label={t("faraday.rename")} title={t("faraday.rename")} onClick={() => { setConfirmDelete(null); setEditing({ id: c.id, title: c.title }); }} className="rounded-lg p-1.5 text-ink/50 hover:bg-ink/5 hover:text-ink"><Pencil className="h-3.5 w-3.5" /></button>
                          <button type="button" aria-label={t("faraday.delete")} title={t("faraday.delete")} onClick={() => setConfirmDelete(asking ? null : c.id)} className={cn("rounded-lg p-1.5 hover:bg-red-50", asking ? "text-red-700" : "text-ink/50 hover:text-red-700")}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    )}
                    {asking ? (
                      <div className="mt-2 flex items-center gap-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs">
                        <span className="flex-1 font-semibold text-red-800">{t("faraday.deleteAsk")}</span>
                        <Button size="sm" variant="danger" onClick={() => { void onDelete(c.id); setConfirmDelete(null); }}>{t("faraday.delete")}</Button>
                        <Button size="sm" variant="outline" onClick={() => setConfirmDelete(null)}>{t("common.cancel")}</Button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <p className="border-t border-ink/10 px-3 py-2 text-[11px] text-ink/50">{t("faraday.historyFooter")}</p>
    </>
  );
}

function groupByDay(items: ConversationSummary[], t: (k: string) => string): Array<{ label: string; items: ConversationSummary[]; showDate: boolean }> {
  const now = new Date();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOf(now);
  const yesterday = today - 86_400_000;
  const week = today - 6 * 86_400_000;
  const buckets: Array<{ label: string; items: ConversationSummary[]; showDate: boolean }> = [
    { label: t("faraday.today"), items: [], showDate: false },
    { label: t("faraday.yesterday"), items: [], showDate: false },
    { label: t("faraday.thisWeek"), items: [], showDate: true },
    { label: t("faraday.earlier"), items: [], showDate: true },
  ];
  for (const c of items) {
    const at = startOf(new Date(c.updatedAt));
    const i = at >= today ? 0 : at >= yesterday ? 1 : at >= week ? 2 : 3;
    buckets[i]!.items.push(c);
  }
  return buckets.filter((b) => b.items.length);
}

/** One level of a confirm card's input as label/value pairs; nested objects and lists show as compact JSON. */
function flatten(input: Record<string, unknown>): Array<[string, string]> {
  return Object.entries(input).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => {
    if (k === "base64" || k === "csv") return [k, `(file, ${String(v).length} characters)`];
    if (typeof v === "object") return [k, JSON.stringify(v).slice(0, 400)];
    return [k, String(v)];
  });
}
